import { Button, Input, Space } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  FormOutlined,
  InboxOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import { getSessionId } from './sessionIdentity.js';
import SessionTitle from './SessionTitle.jsx';

const ARCHIVE_PRESENTATION = {
  confirming: {
    children: '确认',
    title: '再次点击确认留档',
    type: 'default',
  },
  idle: {
    children: null,
    title: '留档会话',
    type: 'text',
  },
  submitting: {
    children: '留档中',
    title: '正在留档会话',
    type: 'default',
  },
};

function resolveArchiveState({
  archiveSubmittingSessionId,
  pendingArchiveSessionId,
  session,
  sessionId,
}) {
  const archived = String(session?.status || '').trim() === 'archived';
  const archiveSubmitting = Boolean(!archived && archiveSubmittingSessionId === sessionId);
  const archiveConfirming = Boolean(
    !archived && pendingArchiveSessionId === sessionId && !archiveSubmitting,
  );
  return { archived, archiveConfirming, archiveSubmitting };
}

function resolveRunState({
  activeSessionId,
  activeSessionRunning,
  loadingSessionId,
  runningSessionIds,
  sessionId,
}) {
  const active = Boolean(sessionId && sessionId === activeSessionId);
  const sessionLoading = Boolean(loadingSessionId && sessionId === loadingSessionId);
  const listedAsRunning = Array.isArray(runningSessionIds)
    && runningSessionIds.includes(sessionId);
  const sessionRunning = [
    sessionLoading,
    active && activeSessionRunning,
    listedAsRunning,
  ].some(Boolean);
  return { active, sessionLoading, sessionRunning };
}

function buildSessionRowState(options) {
  const sessionId = getSessionId(options.session);
  return {
    ...resolveArchiveState({ ...options, sessionId }),
    ...resolveRunState({ ...options, sessionId }),
    isEditing: options.editingSessionId === sessionId,
    sessionId,
  };
}

function rowClassName({ active, nested }) {
  return [
    'flare-session-item',
    nested ? 'is-nested' : '',
    active ? 'is-active' : '',
  ].filter(Boolean).join(' ');
}

function rowButtonStyle({ nested, paneCompact, sessionLoading, themeTokens }) {
  return {
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 10,
    color: themeTokens.textPrimary,
    cursor: sessionLoading ? 'progress' : 'pointer',
    marginLeft: nested ? (paneCompact ? 0 : 2) : 0,
    padding: paneCompact ? '6px 6px' : '7px 8px',
    textAlign: 'left',
    width: nested ? `calc(100% - ${paneCompact ? 0 : 2}px)` : '100%',
  };
}

function archiveButtonStyle({ prominent, themeTokens }) {
  if (!prominent) {
    return {
      color: themeTokens.textSecondary,
      transition: 'all 180ms ease',
    };
  }
  return {
    background: 'rgba(239, 68, 68, 0.18)',
    borderColor: 'rgba(239, 68, 68, 0.28)',
    borderRadius: 999,
    color: '#ef4444',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 180ms ease',
  };
}

function SessionTitleEditor({
  editingTitle, isEditing, onEditingTitleChange, onRenameConfirm, session,
}) {
  if (isEditing) {
    return (
      <Input
        value={editingTitle}
        onChange={(event) => onEditingTitleChange(event.target.value)}
        onPressEnter={() => onRenameConfirm(session)}
        autoFocus
        size="small"
      />
    );
  }
  return null;
}

function RenameActions({
  isEditing,
  onRenameCancel,
  onRenameConfirm,
  onRenameStart,
  session,
  themeTokens,
}) {
  if (!isEditing) {
    return (
      <Button
        icon={<FormOutlined />}
        onClick={() => onRenameStart(session)}
        size="small"
        style={{ color: themeTokens.textSecondary, transition: 'all 180ms ease' }}
        title="编辑会话标题"
        type="text"
      />
    );
  }
  return (
    <>
      <Button
        icon={<CheckOutlined />}
        onClick={() => onRenameConfirm(session)}
        size="small"
        style={{ color: themeTokens.textPrimary, transition: 'all 180ms ease' }}
        title="保存会话标题"
        type="text"
      />
      <Button
        icon={<CloseOutlined />}
        onClick={onRenameCancel}
        size="small"
        style={{ color: themeTokens.textSecondary, transition: 'all 180ms ease' }}
        title="取消编辑"
        type="text"
      />
    </>
  );
}

function ArchiveAction({
  archived,
  archiveConfirming,
  archiveSubmitting,
  onArchiveClick,
  session,
  themeTokens,
}) {
  if (archived) {
    return null;
  }
  const state = archiveSubmitting ? 'submitting' : (archiveConfirming ? 'confirming' : 'idle');
  const presentation = ARCHIVE_PRESENTATION[state];
  const prominent = state !== 'idle';
  return (
    <Button
      disabled={archiveSubmitting}
      icon={prominent ? null : <InboxOutlined />}
      loading={archiveSubmitting}
      onClick={() => onArchiveClick(session)}
      size="small"
      style={archiveButtonStyle({ prominent, themeTokens })}
      title={presentation.title}
      type={presentation.type}
    >
      {presentation.children}
    </Button>
  );
}

function PinAction({ onPinClick, pinSubmitting, session, themeTokens }) {
  if (typeof onPinClick !== 'function') {
    return null;
  }
  const pinned = session?.user_pinned === true;
  const label = pinned ? '取消置顶会话' : '置顶会话';
  return (
    <Button
      aria-label={label}
      disabled={pinSubmitting}
      icon={pinned ? <PushpinFilled /> : <PushpinOutlined />}
      loading={pinSubmitting}
      onClick={() => onPinClick(session)}
      size="small"
      style={{ color: themeTokens.textSecondary, transition: 'all 180ms ease' }}
      title={label}
      type="text"
    />
  );
}

export default function SessionRow(props) {
  const {
    activeSessionId,
    activeSessionRunning,
    archiveSubmittingSessionId,
    editingSessionId,
    editingTitle,
    factLabels,
    loadingSessionId,
    nested,
    onArchiveClick,
    onPinClick,
    onEditingTitleChange,
    onRenameCancel,
    onRenameConfirm,
    onRenameStart,
    onSelectSession,
    paneCompact,
    pendingArchiveSessionId,
    pinSubmittingSessionId,
    runningSessionIds,
    session,
    showFullTitle,
    showTimestamp,
    themeTokens,
  } = props;
  const rowState = buildSessionRowState({
    activeSessionId,
    activeSessionRunning,
    archiveSubmittingSessionId,
    editingSessionId,
    loadingSessionId,
    pendingArchiveSessionId,
    runningSessionIds,
    session,
  });
  const handleSelect = () => {
    if (!rowState.isEditing && !rowState.sessionLoading) {
      onSelectSession?.(rowState.sessionId);
    }
  };
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'minmax(0,1fr) auto',
      }}
    >
      <button
        className={rowClassName({ active: rowState.active, nested })}
        type="button"
        aria-busy={rowState.sessionLoading || undefined}
        onClick={handleSelect}
        style={rowButtonStyle({
          nested,
          paneCompact,
          sessionLoading: rowState.sessionLoading,
          themeTokens,
        })}
      >
        <Space orientation="vertical" size={1} style={{ width: '100%' }}>
          <SessionTitleEditor
            editingTitle={editingTitle}
            isEditing={rowState.isEditing}
            onEditingTitleChange={onEditingTitleChange}
            onRenameConfirm={onRenameConfirm}
            session={session}
          />
          {!rowState.isEditing ? (
            <SessionTitle
              factLabels={factLabels}
              session={session}
              sessionLoading={rowState.sessionLoading}
              sessionRunning={rowState.sessionRunning}
              showFullTitle={showFullTitle}
              showTimestamp={showTimestamp}
            />
          ) : null}
        </Space>
      </button>
      <Space size={0}>
        <PinAction
          onPinClick={onPinClick}
          pinSubmitting={pinSubmittingSessionId === rowState.sessionId}
          session={session}
          themeTokens={themeTokens}
        />
        <RenameActions
          isEditing={rowState.isEditing}
          onRenameCancel={onRenameCancel}
          onRenameConfirm={onRenameConfirm}
          onRenameStart={onRenameStart}
          session={session}
          themeTokens={themeTokens}
        />
        <ArchiveAction
          archived={rowState.archived}
          archiveConfirming={rowState.archiveConfirming}
          archiveSubmitting={rowState.archiveSubmitting}
          onArchiveClick={onArchiveClick}
          session={session}
          themeTokens={themeTokens}
        />
      </Space>
    </div>
  );
}
